import clsx from 'clsx';
import type { ReactNode } from 'react';
import { FiGift, FiLock } from 'react-icons/fi';
import { Link } from 'react-router-dom';

import { lockCopy } from '@/permissions/lock-copy';
import type { GateReason } from '@/permissions/permission-gates';

import { Tooltip } from '../Tooltip/Tooltip';

import s from './FeatureLock.module.scss';

export interface FeatureLockProps {
  /** What is locked, named the way the real section names itself: `Accounting`. */
  title: string;
  /** One line on what the section would do, so the owner knows what they are buying. */
  description?: string;
  /** The section's own icon where there is one; a padlock stands in otherwise. */
  icon?: ReactNode;
  /** The plan that does not carry it — from `useAccessGate().planName`. */
  planName: string;
  /**
   * The plans that do, from `useAccessGate().requiredPlanFor` or `plansLabel`: `Command`,
   * `Navigate or Command`. Empty when none does, and then no upgrade is offered.
   */
  requiredPlan: string;
  /** Why it is locked. Plan by default; see `lockReasonOf`. */
  reason?: GateReason;
  /** Whose seat a seat lock is about — see `lockCopy`. */
  seatOf?: 'self' | 'member';
  /** `null` where this deployment ships no billing; the CTA is then left off. */
  upgradeHref: string | null;
  /** Runs the CTA in place of following `upgradeHref`, for a screen that opens it itself. */
  onAction?: () => void;
  /**
   * The plan is a lifetime one an admin granted, so there is nothing to upgrade to: a note
   * says so where the upgrade link would be.
   */
  planGranted?: boolean;
  /**
   * `page` fills the viewport where a whole route is locked; `card` sits inline where a
   * locked section is one block among others; `inline` is the reason alone, with the full
   * message on hover and focus and the CTA as a text link — a row in the permissions settings.
   */
  variant?: 'page' | 'card' | 'inline';
  /** How many skeleton lines stand in for the real content. */
  rows?: number;
  /**
   * Stretch to the height of whatever it is laid out beside — for a slot in a row of
   * cards, where a placeholder shorter than its neighbours leaves a hole under it and
   * reads as broken rather than as withheld. The skeleton takes up the slack, so the
   * heading stays at the top and the upgrade line at the bottom.
   *
   * Off by default: on its own in a column, a card sized to its content is the right
   * height, and stretching it would only stretch it to nothing in particular.
   */
  fill?: boolean;
  className?: string;
}

/**
 * The shape of a section the owner's plan does not carry, or — `inline` — the reason a single
 * permission is out of reach. Every lock in the app is drawn here, so the copy (`lockCopy`)
 * and the reason it states cannot drift between the locked page and the settings row.
 *
 * Deliberately a placeholder rather than the real component behind a blur: the real one
 * would fetch data the server is going to refuse, and a screen of muted bars reads as
 * "nothing here yet" rather than as content being withheld from view.
 *
 * The page and card variants are rendered only for the owner of the selected vessel — see
 * `AccessVerdict`. Everyone else gets nothing at all, so they never have to explain a limit
 * their reader cannot lift.
 */
export const FeatureLock = ({
  title,
  description,
  icon,
  planName,
  requiredPlan,
  reason = 'plan',
  seatOf,
  upgradeHref,
  onAction,
  planGranted = false,
  variant = 'card',
  rows = 3,
  fill = false,
  className,
}: FeatureLockProps) => {
  const copy = lockCopy({
    reason,
    feature: title,
    currentPlan: planName,
    requiredPlan,
    planGranted,
    seatOf,
  });
  const inline = variant === 'inline';
  const lifetime = planGranted && reason === 'plan';
  const actionClass = inline ? s.inlineAction : s.upgrade;

  const action =
    copy.action &&
    (onAction ? (
      <button type="button" className={actionClass} onClick={onAction}>
        {copy.action}
      </button>
    ) : upgradeHref ? (
      <Link className={actionClass} to={upgradeHref}>
        {copy.action}
      </Link>
    ) : null);

  if (inline) {
    return (
      <span className={clsx(s.inline, className)}>
        <Tooltip label={copy.full} multiline>
          {/* Focusable so the full message reaches a keyboard, which never hovers. */}
          <span className={s.inlineReason} tabIndex={0}>
            <FiLock className={s.chipIcon} aria-hidden />
            {copy.short}
          </span>
        </Tooltip>
        {action}
      </span>
    );
  }

  return (
    <section
      className={clsx(s.lock, variant === 'page' ? s.page : s.card, fill && s.fill, className)}
      aria-labelledby={`feature-lock-${title}`}
    >
      <header className={s.header}>
        <span className={s.icon} aria-hidden>
          {icon ?? <FiLock />}
        </span>
        <div className={s.titleBlock}>
          <h2 id={`feature-lock-${title}`} className={s.title}>
            {title}
          </h2>
          {description && <p className={s.description}>{description}</p>}
        </div>
        <span className={s.chip}>
          {lifetime ? (
            <FiGift className={s.chipIcon} aria-hidden />
          ) : (
            <FiLock className={s.chipIcon} aria-hidden />
          )}
          {lifetime ? 'Lifetime Plan' : copy.short}
        </span>
      </header>

      {/* Purely decorative: it gives the card the silhouette of the real section. */}
      <div className={s.skeleton} aria-hidden>
        {Array.from({ length: rows }, (_, row) => (
          <div key={row} className={s.skeletonRow}>
            <span className={s.skeletonBar} />
            <span className={s.skeletonBarShort} />
          </div>
        ))}
      </div>

      <footer className={s.footer}>
        <p className={s.reason}>{copy.full}</p>
        {action}
      </footer>
    </section>
  );
};
